import React, { ReactNode, useCallback, useEffect } from 'react'
import {
  Button,
  Col,
  Layout as AntLayout,
  notification,
  Row,
  Spin,
  Typography,
  Flex,
  Switch,
  Breadcrumb
} from 'antd'
import './styles.css'
import { MoonOutlined, SunOutlined, LogoutOutlined } from '@ant-design/icons'
import { Link, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import { setNotice } from 'store/notice'
import Clock from '../Clock'
import { useWindowWidth } from '@react-hook/window-size'
import { setConfig } from 'store/config'
import { unsetAuth } from 'store/auth'
import { ItemType } from 'antd/es/breadcrumb/Breadcrumb'
import ProgressLoading from '../ProgressLoading'

const {Header} = AntLayout
const {Text} = Typography

const MOBILE_BREAKPOINT = 700

const ROOT_STYLE = {minHeight: '100vh'}
const LOGO_STYLE = {fontSize: '20px'}
const BREADCRUMB_STYLE = {width: '100%'}
const SPIN_STYLE = {height: '50vh'}

const NOTIFICATION_CONFIG = {placement: 'top', maxCount: 1, stack: false} as const

const Layout = ({children, loading = false, title, breadcrumb}: {
  children?: ReactNode;
  loading?: boolean;
  title?: string;
  breadcrumb?: ItemType[]
}) => {
  const navigate = useNavigate()
  const windowWidth = useWindowWidth()
  const isMobile = windowWidth < MOBILE_BREAKPOINT

  const dispatch = useAppDispatch()

  // Точечные селекторы вместо целых срезов: Layout сидит на каждой странице,
  // а под ним висит график — лишние рендеры тут дорогие.
  const hasToken = useAppSelector((store) => !!store.auth.token)
  const noticeData = useAppSelector((state) => state.notice)
  const useDarkTheme = useAppSelector((state) => state.config.useDarkTheme)

  const [ notice, contextNotice ] = notification.useNotification(NOTIFICATION_CONFIG)

  useEffect(() => {
    document.title = title && !loading ? `${ title } - POLY` : 'POLY'
  }, [ title, loading ])

  useEffect(() => {
    if (!noticeData?.message) return

    notice[noticeData.type || 'open']({message: noticeData.message})
    dispatch(setNotice({}))
  }, [ noticeData, notice, dispatch ])

  const logout = useCallback(() => {
    dispatch(unsetAuth())
    navigate('/')
  }, [ dispatch, navigate ])

  const toggleTheme = useCallback(
    (value: boolean) => dispatch(setConfig({useDarkTheme: !value})),
    [ dispatch ]
  )

  const contentStyle = {padding: 15, minHeight: '100%', marginBottom: isMobile ? '100px' : 0}

  return (
    <AntLayout style={ ROOT_STYLE }>
      <Header>
        <Row justify="space-between" align="middle">
          <Col span={ 8 }>
            <Flex gap={ 10 } align="center">
              <Link to="/" style={ LOGO_STYLE }>
                <Text>POLY</Text>
              </Link>
              <Switch
                checked={ !useDarkTheme }
                onChange={ toggleTheme }
                checkedChildren={ <SunOutlined/> }
                unCheckedChildren={ <MoonOutlined/> }
              />
            </Flex>
          </Col>
          <Col span={ 8 }>
            <Flex gap={ 10 } align="center" justify="center"><Clock/></Flex>
          </Col>
          <Col span={ 8 }>
            { hasToken && (
              <Flex gap={ 10 } align="center" justify="right">
                <Button shape="circle" icon={ <LogoutOutlined/> } onClick={ logout }/>
              </Flex>
            ) }
          </Col>
        </Row>
      </Header>
      <AntLayout>
        <ProgressLoading loading={ loading }/>
        <AntLayout style={ contentStyle }>
          <Row justify="center" gutter={ [ 20, 20 ] }>
            { !!breadcrumb?.length && (
              <Col span={ 24 }>
                <Breadcrumb items={ breadcrumb } style={ BREADCRUMB_STYLE }/>
              </Col>
            ) }
            <Col span={ 24 }>
              { loading ? <Spin style={ SPIN_STYLE } className="spin"/> : children }
            </Col>
            <Col span={ 24 }>
              { contextNotice }
            </Col>
          </Row>
        </AntLayout>
      </AntLayout>
    </AntLayout>
  )
}

export default Layout
